/**
 * Copy trading, as a page of its own: who you copy, whom you invited to copy you, and — for whoever
 * runs the server — whether copied orders are really sent. Being copied is decided person by person:
 * you invite someone who has an account here; they start it themselves, with their own money.
 * The same body is the "Copy trading" section of Settings.
 */
import { useEffect, useState } from 'react'
import type { CopyConsent, CopyPerson } from '@shared/copy'
import { copyApi } from './api'
import { Person, StartCopy, useCopies } from './Copy'
import { Btn, Dialog, Field, Status, Switch, askSure, cx, inputCls, usd } from './kit'
import { Bar, Page, useTabs } from './Page'
import { Group, Row, Rows, note, quiet } from './rows'
import { useDemo } from './store'

const day = (ms: number) => new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
const fail = (e: unknown) => useDemo.getState().say(e instanceof Error ? e.message : 'That did not work')

/** Choose who may copy you: whoever runs the server picks from everyone here; anyone else names a person by their username. */
function InviteToCopy({ taken, onClose, onDone }: { taken: string[]; onClose: () => void; onDone: () => void }) {
  const say = useDemo((s) => s.say)
  const [people, setPeople] = useState<CopyPerson[] | null>(null)
  const [username, setUsername] = useState('')
  const [busy, setBusy] = useState(false)
  // being copied has terms of its own: read and accepted with every invitation
  const [terms, setTerms] = useState<CopyConsent | null>(null)
  const [agreed, setAgreed] = useState(false)
  useEffect(() => {
    void copyApi.people().then(setPeople, () => setPeople([]))
    void copyApi.leaderTerms().then(setTerms, () => setTerms(null))
  }, [])
  const ready = !!terms && agreed && !busy
  const invite = (who: { userId?: string; username?: string }) => {
    if (!terms || !agreed) return
    setBusy(true)
    void copyApi.invite(who, terms.version).then(
      (p) => (say(`${p.name} can copy your trades now — they start it themselves`), onDone(), onClose()),
      (e) => (fail(e), setBusy(false)),
    )
  }
  const list = (people ?? []).filter((p) => !taken.includes(p.id))
  return (
    <Dialog
      open
      onClose={onClose}
      title="Invite someone to copy you"
      width={560}
      footer={
        <Btn variant="outline" onPanel onClick={onClose}>
          Close
        </Btn>
      }
    >
      <div className="space-y-4">
        <div className="text-[12px] leading-[18px] tracking-normal text-ws-fg-3">They see you under “People you copy” and start it themselves: with their own money, in their own account, by limits they set. You never see their money and place no order for them.</div>
        <div>
          <h3 className="mb-[6px] text-[12px] font-normal leading-4 tracking-normal text-ws-label">What you are agreeing to</h3>
          <div className="max-h-[200px] overflow-y-auto whitespace-pre-wrap rounded-[8px] border border-ws-line px-3 py-[10px] text-[12px] leading-[18px] tracking-normal text-ws-fg-2">{terms?.text ?? 'Loading…'}</div>
          <label className="mt-3 flex cursor-pointer items-start gap-3 text-[13px] leading-5 tracking-normal text-ws-fg-2">
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-[3px] h-4 w-4 shrink-0 accent-white" />
            I have read this. I am not paid for being copied, I give no advice and I promise no result.
          </label>
        </div>
        {list.length > 0 && (
          <Rows>
            {list.map((p) => (
              <Row
                key={p.id}
                left={<Person name={p.name} />}
                title={p.name}
                sub={`@${p.username}`}
                right={
                  <button type="button" disabled={!ready} title={agreed ? undefined : 'Read and accept the terms above first'} className={cx(quiet, 'disabled:opacity-40')} onClick={() => invite({ userId: p.id })}>
                    Invite
                  </button>
                }
              />
            ))}
          </Rows>
        )}
        {people && !people.length && (
          <Field label="Their username here" hint="They need an account on this server">
            <div className="flex gap-2">
              <input value={username} onChange={(e) => setUsername(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && username.trim() && invite({ username })} autoComplete="off" spellCheck={false} placeholder="username" className={inputCls} />
              <Btn variant="white" className="shrink-0 px-3" disabled={!ready || !username.trim()} onClick={() => invite({ username })}>
                Invite
              </Btn>
            </div>
          </Field>
        )}
        {people && people.length > 0 && !list.length && <div className="text-[13px] tracking-normal text-ws-fg-3">Everyone with an account here is already invited.</div>}
      </div>
    </Dialog>
  )
}

/** The server's own switch, for whoever runs it: rehearsal (nothing is sent) or live. */
function CopyMode({ live, onChanged }: { live: boolean; onChanged: () => void }) {
  const say = useDemo((s) => s.say)
  const set = (on: boolean) => void copyApi.setMode(on).then(() => (say(on ? 'Copying is live: follower orders are sent' : 'Copying is in rehearsal: nothing is sent'), onChanged()), fail)
  return (
    <Group title="This server" note="Only you see this">
      <Rows>
        <Row
          title="Send copied orders for real"
          sub={live ? 'Live: a follower’s copy sends real orders to their own broker' : 'Rehearsal: every copied trade is worked out and written down, none is sent'}
          right={
            <Switch
              on={live}
              onChange={(v) => (v ? askSure({ title: 'Go live with copy trading?', body: 'From now on, when someone copies a trade, a real order goes to their own brokerage account. Watch a rehearsal first: a copy’s page shows what it would have sent.', cta: 'Go live', done: () => set(true) }) : set(false))}
              label="Send copied orders for real"
            />
          }
        />
      </Rows>
    </Group>
  )
}

const FOLLOWER: Record<string, { tone: 'free' | 'warn' | 'off'; text: string }> = { active: { tone: 'free', text: 'Copying' }, paused: { tone: 'off', text: 'Paused' }, stopping: { tone: 'warn', text: 'Stopping' }, stopped: { tone: 'off', text: 'Stopped' }, invited: { tone: 'off', text: 'Invited, not started' } }

/** Who you copy, whom you invited to copy you, how it works. */
export function CopyHub() {
  const { data, load } = useCopies()
  const admin = useDemo((s) => s.user?.role === 'admin')
  const openCopy = useDemo((s) => s.openCopy)
  const go = useDemo((s) => s.go)
  const [leader, setLeader] = useState<{ id: string; name: string } | null>(null)
  const [inviting, setInviting] = useState(false)
  if (!data) return <div className="h-24 animate-ws-pulse rounded-[8px] border border-ws-line" />
  const mine = data.copies.filter((c) => c.status !== 'stopped')
  const ended = data.copies.filter((c) => c.status === 'stopped')
  const free = data.leaders.filter((l) => !l.copying)
  return (
    <>
      {!data.live && (
        <div className="rounded-[8px] border border-ws-line px-3 py-[10px] text-[12px] leading-[18px] tracking-normal text-ws-fg-3">
          <span className="text-ws-warn">Rehearsal.</span> Copying is being tried out on this server: every copied trade is worked out and written down, and nothing is sent to a broker.
        </div>
      )}
      <Group title="People you copy" note="Each is its own sub-account in the portfolio">
        <Rows>
          {mine.map((c) => (
            <Row
              key={c.id}
              left={<Person name={c.leader.name} />}
              title={c.leader.name}
              sub={`Since ${day(c.since)} · ${usd(c.putIn)} put in · worth ${usd(c.value)} · ${c.gain >= 0 ? '+' : '−'}${usd(Math.abs(c.gain))}`}
              right={<span className={cx('shrink-0 text-[12px] tracking-normal', c.gain > 0 ? 'text-ws-up' : c.gain < 0 ? 'text-ws-down' : 'text-ws-fg-3')}>{`${c.gainPct >= 0 ? '+' : '−'}${Math.abs(c.gainPct).toFixed(2)}%`}</span>}
              onClick={() => openCopy(c.id)}
            />
          ))}
          {free.map((l) => (
            <Row
              key={l.id}
              left={<Person name={l.name} />}
              title={l.name}
              sub="Invited you to copy their trades"
              right={
                <button type="button" className={quiet} onClick={() => setLeader(l)}>
                  Start copying
                </button>
              }
            />
          ))}
          {ended.map((c) => (
            <Row key={c.id} left={<Person name={c.leader.name} />} title={c.leader.name} sub={`Stopped · it made ${c.gain >= 0 ? '+' : '−'}${usd(Math.abs(c.gain))}`} right={<span className="shrink-0 text-[12px] tracking-normal text-ws-fg-4">Ended</span>} onClick={() => openCopy(c.id)} />
          ))}
          {!mine.length && !free.length && !ended.length && <Row title="Nobody yet" sub="Someone who invites you to copy their trades shows up here" />}
        </Rows>
        <p className={note}>You choose how much money follows a person and your own limits. That money is kept apart from the rest of your account, and you can stop at any time.</p>
      </Group>

      <Group title="People who may copy you" note="You choose them, one by one">
        <Rows>
          {data.followers.map((f) => (
            <Row
              key={f.id}
              left={<Person name={f.name} />}
              title={f.name}
              sub={f.status === 'invited' ? `Invited ${day(f.since)}` : `Copying since ${day(f.since)}`}
              right={
                <span className="flex shrink-0 items-center gap-2">
                  <Status tone={FOLLOWER[f.status].tone}>{FOLLOWER[f.status].text}</Status>
                  <button
                    type="button"
                    className={quiet}
                    onClick={() =>
                      askSure({
                        title: `Take back ${f.name}’s invitation?`,
                        body: f.status === 'invited' ? 'They can no longer start copying you.' : 'Nothing new you buy is copied by them. What their copy already holds is theirs: your sales are still mirrored there, and they can stop it whenever they want.',
                        cta: 'Take back',
                        done: () => void copyApi.uninvite(f.id).then(load, fail),
                      })
                    }
                  >
                    Take back
                  </button>
                </span>
              }
            />
          ))}
          <Row
            title="Invite someone to copy you"
            sub="Someone who already has an account here"
            right={
              <button type="button" className={quiet} onClick={() => setInviting(true)}>
                Invite
              </button>
            }
          />
        </Rows>
        <p className={note}>
          You never see what they hold or how much they put in, and you place no order for them: their own account copies yours, by rules they set and can stop at any time.
          {admin && (
            <>
              {' '}
              Someone who has no account yet gets one in{' '}
              <button type="button" className="underline decoration-ws-rule underline-offset-2 hover:text-ws-fg" onClick={() => (go('settings'), useTabs.getState().set({ settings: 'people' }))}>
                Settings → People
              </button>
              .
            </>
          )}
        </p>
      </Group>

      <Group title="How it works">
        <Rows>
          <Row left={<span className="w-4 shrink-0 text-center text-[12px] tabular-nums text-ws-fg-4">1</span>} title="The one to be copied invites the other" sub="Above: “Invite someone to copy you”. Both need an account here and a broker of their own" />
          <Row left={<span className="w-4 shrink-0 text-center text-[12px] tabular-nums text-ws-fg-4">2</span>} title="The one who copies starts it" sub="Under “People you copy”: Start copying, with an amount of their own money and their own limits" />
          <Row left={<span className="w-4 shrink-0 text-center text-[12px] tabular-nums text-ws-fg-4">3</span>} title="From then on it follows by itself" sub="A buy takes the same share of the copy’s money as it took of the copied account; a sale sells the same share of what the copy holds" />
        </Rows>
      </Group>
      {admin && <CopyMode live={data.live} onChanged={load} />}
      <StartCopy leader={leader} free={data.free} onClose={() => setLeader(null)} onStarted={load} />
      {inviting && <InviteToCopy taken={data.followers.map((f) => f.id)} onClose={() => setInviting(false)} onDone={load} />}
    </>
  )
}

export const CopyBar = () => <Bar>{<span className="px-2 text-[13px] tracking-normal text-ws-fg-3">Follow someone’s trades with your own money and your own limits</span>}</Bar>
export function CopyView() {
  return (
    <Page className="mx-auto max-w-[720px]">
      <div className="space-y-6">
        <CopyHub />
      </div>
    </Page>
  )
}
